import { z } from 'zod';

import { baseOrganizationEntitySchema } from './organizationContract';
import { urlSchema } from './schemaUtils';
import { websiteDiscoverySchema } from './websiteDiscovery';
import {
  RemovalBehavior,
  SourceType,
  UpdateFrequency,
  WebsitePageStatus,
} from '../enums';
import { normalizeWebsiteUrl } from '../shared';

export const paginationSchema = z.object({
  page: z.number().positive().optional(),
  pageSize: z.number().positive().optional(),
});

export const websiteSyncModeSchema = z.enum(['crawl', 'crawl_and_refresh']);
export type WebsiteSyncMode = z.infer<typeof websiteSyncModeSchema>;
export const triggerWebsiteSyncSchema = z.object({
  id: z.string().uuid(),
  mode: websiteSyncModeSchema.default('crawl'),
});

/** Non-inherited settings stored on managed source roots in the existing JSONB column. */
export const websiteSourceConfigSchema = z
  .object({
    sourceType: z.literal(SourceType.Website),
    discovery: websiteDiscoverySchema.optional(),
  })
  .strict();

export const googleDriveSourceConfigSchema = z
  .object({
    sourceType: z.literal(SourceType.GoogleDrive),
    includeSubfolders: z.boolean(),
  })
  .strict();

export const rssSourceConfigSchema = z
  .object({
    sourceType: z.literal(SourceType.Rss),
    contentMode: z.enum(['inline', 'follow_link']),
  })
  .strict();

export const sourceConfigSchema = z.discriminatedUnion('sourceType', [
  websiteSourceConfigSchema,
  googleDriveSourceConfigSchema,
  rssSourceConfigSchema,
]);

export type SourceConfig = z.infer<typeof sourceConfigSchema>;

export const documentFolderSchema = baseOrganizationEntitySchema.extend({
  name: z.string().min(1).max(255),
  description: z.string().max(2000).nullable().optional(),
  defaultWeight: z.number().min(0).max(1),
  availableForAllAgents: z.boolean(),
  parent: z.string().uuid().nullable().optional(),
  chats: z.array(z.string().uuid()),
  effectiveChatIds: z.array(z.string().uuid()).optional(),
  effectiveAvailableForAllAgents: z.boolean().optional(),
  /**
   * Origin of the folder's content. A non-`upload` value means the folder
   * belongs to a managed source tree (master plan §3.2).
   */
  sourceType: z.nativeEnum(SourceType).optional(),
  /**
   * External handle of the source root (§3.2); non-null ⇒ managed root.
   * Interpreted per `sourceType` — a full URL for website/RSS, a bare Drive
   * folder ID for `google_drive` — so it is not URL-validated here.
   */
  sourceUri: z.string().nullable().optional(),
  sourceConfig: sourceConfigSchema.nullable().optional(),
  websitePathPrefix: z.string().nullable().optional(),
  updateFrequency: z.nativeEnum(UpdateFrequency).nullable().optional(),
  discoveryFrequency: z.nativeEnum(UpdateFrequency).nullable().optional(),
  changeThreshold: z.number().min(0).max(1).nullable().optional(),
  autoIncludeNewPages: z.boolean().nullable().optional(),
  removalBehavior: z.nativeEnum(RemovalBehavior).nullable().optional(),
  // --- Sync health / schedule stamps (managed source roots) ---
  lastSyncedAt: z.coerce.date().nullable().optional(),
  nextUpdateAt: z.coerce.date().nullable().optional(),
  lastCheckedAt: z.coerce.date().nullable().optional(),
  lastSyncError: z.string().nullable().optional(),
});

export const createDocumentFolderSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(2000).optional(),
  defaultWeight: z.number().min(0).max(1).optional(),
  parentId: z.string().uuid().nullable().optional(),
  availableForAllAgents: z.boolean().optional(),
  chatIds: z.array(z.string().uuid()).optional(),
});

export const createWebsiteFolderSchema = z.object({
  discovery: websiteDiscoverySchema.optional(),
  url: z.string().url(),
  name: z.string().optional(),
  parentId: z.string().uuid().nullable().optional(),
  updateFrequency: z.nativeEnum(UpdateFrequency).nullable().default(null),
  discoveryFrequency: z.nativeEnum(UpdateFrequency).nullable().default(null),
  autoIncludeNewPages: z.boolean().default(false),
  removalBehavior: z.nativeEnum(RemovalBehavior).default(RemovalBehavior.Flag),
  availableForAllAgents: z.boolean().default(false),
  chatIds: z.array(z.string().uuid()).default([]),
  changeThreshold: z.number().min(0).max(1).optional(),
  includePageUrls: z.array(z.string().url()).default([]),
  allDiscoveredUrls: z.array(z.string().url()).default([]),
  weights: z.record(z.string(), z.number().min(0).max(1)).default({}),
});

export const updateDocumentFolderSchema = z.object({
  discovery: websiteDiscoverySchema.optional(),
  id: z.string().uuid(),
  includeSubfolders: z.boolean().optional(),
  name: z.string().min(1).max(255).optional(),
  description: z.string().max(2000).nullable().optional(),
  defaultWeight: z.number().min(0).max(1).optional(),
  availableForAllAgents: z.boolean().optional(),
  updateFrequency: z.nativeEnum(UpdateFrequency).nullable().optional(),
  discoveryFrequency: z.nativeEnum(UpdateFrequency).nullable().optional(),
  changeThreshold: z.number().min(0).max(1).nullable().optional(),
  autoIncludeNewPages: z.boolean().nullable().optional(),
  removalBehavior: z.nativeEnum(RemovalBehavior).nullable().optional(),
});

export const moveDocumentFolderSchema = z.object({
  id: z.string().uuid(),
  newParentId: z.string().uuid().nullable(),
});

export const assignFolderToChatsSchema = z.object({
  folderId: z.string().uuid(),
  chatIds: z.array(z.string().uuid()),
  availableForAllAgents: z.boolean().optional(),
});

export const bulkAssignFoldersToChatsSchema = z.object({
  folderIds: z.array(z.string().uuid()).min(1),
  chatIds: z.array(z.string().uuid()),
  availableForAllAgents: z.boolean(),
});

export const bulkUpdateFolderAutoUpdateSchema = z
  .object({
    folderIds: z.array(z.string().uuid()).min(1),
    updateFrequency: z.nativeEnum(UpdateFrequency).nullable().optional(),
    discoveryFrequency: z.nativeEnum(UpdateFrequency).nullable().optional(),
    changeThreshold: z.number().min(0).max(1).nullable().optional(),
    autoIncludeNewPages: z.boolean().nullable().optional(),
    removalBehavior: z.nativeEnum(RemovalBehavior).nullable().optional(),
  })
  .refine(
    data =>
      data.updateFrequency !== undefined ||
      data.discoveryFrequency !== undefined ||
      data.changeThreshold !== undefined ||
      data.autoIncludeNewPages !== undefined ||
      data.removalBehavior !== undefined,
    {
      message: 'At least one field must be provided for update',
    },
  );

export const removeFolderFromChatSchema = z.object({
  folderId: z.string().uuid(),
  chatId: z.string().uuid(),
});

export const documentPipelineFilterSchema = z.enum([
  'processing',
  'errored',
  'done',
  'parsing',
  'embedding',
  'parsingError',
  'embeddingError',
]);

export type DocumentPipelineFilter = z.infer<
  typeof documentPipelineFilterSchema
>;

export const KB_TRACKED_PAGE_FILTER = 'tracked' as const;

export type KBWebsitePageFilter =
  | WebsitePageStatus
  | typeof KB_TRACKED_PAGE_FILTER;

/**
 * Source lens (master plan §1A / Phase K4a): filters both folders and
 * documents in `findContents` by their own `sourceType`. Orthogonal to
 * `status`/`documentPipelineFilter` — composes with either. Exported
 * standalone so the frontend route can parse the same values from the URL.
 */
export const sourceTypeFilterSchema = z.nativeEnum(SourceType);

export const getFolderContentsSchema = z.object({
  folderId: z.string().uuid().nullable(),
  status: z.nativeEnum(WebsitePageStatus).optional(),
  pageHasDocument: z.boolean().optional(),
  search: z.string().optional(),
  chatId: z.string().uuid().optional(),
  documentPipelineFilter: documentPipelineFilterSchema.optional(),
  sourceType: sourceTypeFilterSchema.optional(),
  pagination: paginationSchema.optional(),
  contentUpdatedSort: z.enum(['asc', 'desc']).optional(),
});

export const accessPropagationStatusSchema = getFolderContentsSchema
  .pick({ folderId: true, chatId: true })
  .extend({ documentId: z.string().uuid().optional() });

export const findContentsMetaSchema = z.object({
  page: z.number(),
  pageSize: z.number(),
  pageCount: z.number(),
  total: z.number(),
  hasMore: z.boolean(),
});

export type FindContentsMeta = z.infer<typeof findContentsMetaSchema>;
export type PaginationParams = z.infer<typeof paginationSchema>;

export const websitePageActionSchema = z.object({
  pageIds: z.array(z.string().uuid()).min(1),
  chatIds: z.array(z.string().uuid()).optional(),
  availableForAllAgents: z.boolean().optional(),
});

export const excludeFromKbSchema = z
  .object({
    pageIds: z.array(z.string().uuid()).optional().default([]),
    documentIds: z.array(z.string().uuid()).optional().default([]),
    folderIds: z.array(z.string().uuid()).optional().default([]),
  })
  .refine(
    data =>
      data.pageIds.length > 0 ||
      data.documentIds.length > 0 ||
      data.folderIds.length > 0,
    {
      message:
        'At least one of pageIds, documentIds, or folderIds must be non-empty',
    },
  );

export type ExcludeFromKbDTO = z.infer<typeof excludeFromKbSchema>;

export const previewWebsiteCrawlSchema = z.object({
  discovery: websiteDiscoverySchema.optional(),
  url: z.string().url(),
});

export const checkWebsiteRootOverlapSchema = z.object({
  url: z.string().min(1).transform(normalizeWebsiteUrl).pipe(z.string().url()),
});

export type CheckWebsiteRootOverlapDTO = z.infer<
  typeof checkWebsiteRootOverlapSchema
>;

export const previewRssFeedSchema = z.object({
  url: urlSchema,
});

export const createRssFolderSchema = z.object({
  url: urlSchema,
  name: z.string().optional(),
  parentId: z.string().uuid().nullable().optional(),
  /** Overrides the auto-detected default from the test-feed preview. */
  contentMode: z.enum(['inline', 'follow_link']).optional(),
  updateFrequency: z.nativeEnum(UpdateFrequency).nullable().default(null),
  availableForAllAgents: z.boolean().default(false),
  chatIds: z.array(z.string().uuid()).default([]),
});

/**
 * Create a Google Drive public-folder sync source (Phase K2). `shareLink` is
 * a folder share URL or bare folder ID; the router extracts the canonical
 * folder ID into `DocumentFolder.sourceUri` and stores `includeSubfolders`
 * in `sourceConfig`. Removal default is `Flag` per the master plan's Phase
 * K2 resolution.
 */
export const createDriveFolderSchema = z.object({
  shareLink: z.string().min(1),
  name: z.string().optional(),
  parentId: z.string().uuid().nullable().optional(),
  includeSubfolders: z.boolean().default(false),
  updateFrequency: z.nativeEnum(UpdateFrequency).nullable().default(null),
  removalBehavior: z.nativeEnum(RemovalBehavior).default(RemovalBehavior.Flag),
  availableForAllAgents: z.boolean().default(false),
  chatIds: z.array(z.string().uuid()).default([]),
  changeThreshold: z.number().min(0).max(1).optional(),
});

export const previewDriveFolderSchema = z.object({
  shareLink: z.string().min(1),
  includeSubfolders: z.boolean().default(false),
});

export const promoteToTrackedWebsiteSchema = z.object({
  documentIds: z.array(z.string().uuid()).min(1),
  targetHost: z.string().min(1),
  /**
   * Path scope for the tracked root, e.g. `/blog` or `` for whole host.
   * Normalized to leading slash, no trailing slash.
   */
  pathPrefix: z
    .string()
    .optional()
    .transform(value => {
      if (value == null) {
        return '';
      }

      const trimmed = value.trim();

      if (!trimmed || trimmed === '/') {
        return '';
      }

      const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;

      return withSlash.replace(/\/+$/, '') || '';
    }),
  moveAllHostDocuments: z.boolean().default(false),
  updateFrequency: z.nativeEnum(UpdateFrequency).optional(),
  discoveryFrequency: z.nativeEnum(UpdateFrequency).optional(),
  autoIncludeNewPages: z.boolean().optional(),
  removalBehavior: z.nativeEnum(RemovalBehavior).optional(),
  availableForAllAgents: z.boolean().optional(),
  chatIds: z.array(z.string().uuid()).optional(),
});

export type DocumentFolderDTO = z.infer<typeof documentFolderSchema>;
export type CreateDocumentFolderDTO = z.infer<
  typeof createDocumentFolderSchema
>;
export type CreateWebsiteFolderDTO = z.infer<typeof createWebsiteFolderSchema>;
export type UpdateDocumentFolderDTO = z.infer<
  typeof updateDocumentFolderSchema
>;
export type MoveDocumentFolderDTO = z.infer<typeof moveDocumentFolderSchema>;
export type AssignFolderToChatsDTO = z.infer<typeof assignFolderToChatsSchema>;
export type BulkAssignFoldersToChatsDTO = z.infer<
  typeof bulkAssignFoldersToChatsSchema
>;
export type BulkUpdateFolderAutoUpdateDTO = z.infer<
  typeof bulkUpdateFolderAutoUpdateSchema
>;
export type RemoveFolderFromChatDTO = z.infer<
  typeof removeFolderFromChatSchema
>;
export type GetFolderContentsDTO = z.infer<typeof getFolderContentsSchema>;
export type WebsitePageActionDTO = z.infer<typeof websitePageActionSchema>;
export type PreviewWebsiteCrawlDTO = z.infer<typeof previewWebsiteCrawlSchema>;
export type CreateDriveFolderDTO = z.infer<typeof createDriveFolderSchema>;
export type PreviewDriveFolderDTO = z.infer<typeof previewDriveFolderSchema>;
export type PromoteToTrackedWebsiteDTO = z.infer<
  typeof promoteToTrackedWebsiteSchema
>;
export type PreviewRssFeedDTO = z.infer<typeof previewRssFeedSchema>;
export type CreateRssFolderDTO = z.infer<typeof createRssFolderSchema>;
